import React from 'react';
import { Phone, Mail, MessageCircle, Linkedin, Eye } from 'lucide-react';

interface PipelineTableProps {
  leads: any[];
  onViewDetails: (lead: any) => void;
}

const PipelineTable: React.FC<PipelineTableProps> = ({ leads, onViewDetails }) => {
  const getChannelIcon = (channel: string) => {
    console.log('[PipelineTable] Processing channel:', channel);
    
    if (!channel || channel.trim() === '') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
          <MessageCircle size={12} className="mr-1" />
          Unknown
        </span>
      );
    }

    const normalizedChannel = channel.toString().toLowerCase().trim();
    console.log('[PipelineTable] Normalized channel:', normalizedChannel);
    
    switch (normalizedChannel) {
      case 'email':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <Mail size={12} className="mr-1" />
            Email
          </span>
        );
      case 'call':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Phone size={12} className="mr-1" />
            Call
          </span>
        );
      case 'whatsapp':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <MessageCircle size={12} className="mr-1" />
            WhatsApp
          </span>
        );
      case 'linkedin':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Linkedin size={12} className="mr-1" />
            LinkedIn
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            <MessageCircle size={12} className="mr-1" />
            {channel}
          </span>
        );
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage?.toLowerCase().replace(/\s+/g, '_')) {
      case 'engaged':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'interested':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'qualified':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'meeting_booked':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'proposal_sent':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'closed_won':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'closed_lost':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = (status || '').toLowerCase().trim();
    
    switch (normalizedStatus) {
      case 'chatting':
      case 'talking':
        return 'bg-green-50 text-green-700 border border-green-200';
      case 'follow up':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'booked':
        return 'bg-green-100 text-green-700 border border-green-300';
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'limited':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    if (!timestamp || timestamp === 'N/A' || timestamp.trim() === '' || timestamp === 'undefined') return 'N/A';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'N/A';
      
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Person
              </th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stage
              </th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Activity
              </th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Channel
              </th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {leads.map((lead, index) => (
              <tr key={lead._rowIndex || lead.id || index} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium text-gray-900">
                      {lead.company_name || 'Unknown Company'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {lead.industry || 'Unknown Industry'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">
                    {lead.dm_full_name || lead.contact_person || 'Unknown'}
                    {lead.dm_full_name && lead.contact_person && lead.dm_full_name !== lead.contact_person && (
                      <span className="text-gray-500 text-sm"> (partial)</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStageColor(lead.stage)}`}>
                    {lead.stage || 'Unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-900">
                  {formatTimeAgo(lead.last_reply_timestamp)}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    {getChannelIcon(lead.channel)}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                    {lead.status || 'Open'}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => onViewDetails(lead)}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {leads.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No leads found</div>
          <div className="text-gray-400 text-sm mt-1">
            Try adjusting your filters or search terms
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineTable;